import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { generateNurturingPlan } from 'app/apiService/nurturingPlan/nurturingPlanApiService';
import { fetchLeadAnalysis } from 'app/apiService/leads/leadAnalysisApiService';
import {
  fetchAvailablePlanSegments,
  fetchNurturingPlanInitializeReadiness,
  fetchNurturingPlanSettings,
  launchDealerForNurturingPlan,
  initializeNurturingPlanSettings,
  initializeNurturingPlanSettingsDefault,
  publishNurturingPlan,
  saveNurturingPlanSettings,
} from 'app/apiService/nurturingPlan/nurturingPlanSettingsDataService';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { headerActions } from 'app/pages/Header/slice';
import { parseCalendarDateFromApi } from 'utils/dateUtils';
import {
  isDealerLaunchMaterializationInProgress,
  mergeDealerWithLaunchAck,
  persistSelectedDealerToLocalStorage,
  shouldShowDealerLaunchMaterializationStatus,
} from 'utils/dealerLaunchMaterialization';

import { firstUnusedSegmentName } from '../utils/dealerSegmentOptions';
import type { GeneratedPlan, PlanConfig, Segment, WarmupStep } from '../types';
import { segsWithCounts } from '../utils/nurturingPlanEngine';
import {
  buildSavePayload,
  EMPTY_RAW_CONFIG,
  extractGeneratedPlan,
  extractSettingsBody,
  requestBodyToConfig,
} from '../utils/planSettingsMapping';
import {
  addSegmentWithName,
  deleteSegment,
  rebalancePct,
  reorderSegments,
} from '../utils/segmentRebalance';
import { mapLeadAnalysisToPlanFields } from '../utils/leadAnalysisMapping';
import { useNurturingPlanVersions } from './useNurturingPlanVersions';

type RawConfig = Omit<PlanConfig, 'segs'>;

export type PlanTab = 'summary' | 'phases' | 'email' | 'loading' | 'daily';

interface EditableConfig {
  rawConfig: RawConfig;
  segments: Segment[];
}

const configSnapshotKey = (snap: EditableConfig) =>
  JSON.stringify({ rawConfig: snap.rawConfig, segments: snap.segments });

const resolveDealerId = (selectedDealer: unknown): string | undefined => {
  if (typeof selectedDealer !== 'object' || selectedDealer === null) return undefined;
  const dealer = selectedDealer as { dealer?: string; _id?: string };
  return dealer.dealer || dealer._id || undefined;
};

const resolveLaunchDateRaw = (dealer: unknown): string | null => {
  if (typeof dealer !== 'object' || dealer === null) return null;
  const raw = (dealer as { launch_date?: unknown })?.launch_date;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed || null;
  }
  if (raw instanceof Date) return raw.toISOString();
  return String(raw);
};

const notify = (
  severity: 'success' | 'error',
  message: string,
) =>
  notificationAlertActions.addNotification({
    open: true,
    severity,
    message,
  });

export const useNurturingPlan = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const selectedDealer = useSelector(selectDealerSelected);
  const dealerId = resolveDealerId(selectedDealer);

  const launchDateRaw = resolveLaunchDateRaw(selectedDealer);
  const parsedLaunchDate = launchDateRaw
    ? parseCalendarDateFromApi(launchDateRaw)
    : null;
  const hasLaunchDate = Boolean(parsedLaunchDate?.isValid);
  const showLaunchMaterializationStatus =
    shouldShowDealerLaunchMaterializationStatus(selectedDealer);
  const isLaunchMaterializationInProgress =
    isDealerLaunchMaterializationInProgress(selectedDealer);
  const launchDateDisplay = hasLaunchDate
    ? parsedLaunchDate!.toFormat('MM/dd/yyyy')
    : null;

  const [rawConfig, setRawConfig] = useState<RawConfig>({ ...EMPTY_RAW_CONFIG });
  const [segments, setSegments] = useState<Segment[]>([]);
  const [original, setOriginal] = useState<EditableConfig>({
    rawConfig: { ...EMPTY_RAW_CONFIG },
    segments: [],
  });
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  const [segmentsConfigError, setSegmentsConfigError] = useState<string | null>(null);
  const [segmentsConfigLoading, setSegmentsConfigLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<PlanTab>('summary');
  const [openBatches, setOpenBatches] = useState<Record<string, boolean>>({});
  const [plan, setPlan] = useState<GeneratedPlan | null>(null);
  /** Last saved/loaded plan snapshot — restored on Cancel. */
  const [persistedPlan, setPersistedPlan] = useState<GeneratedPlan | null>(null);
  /** Left-panel state the current displayed plan was generated from (null = not yet generated). */
  const [lastGeneratedSnapshot, setLastGeneratedSnapshot] =
    useState<EditableConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [initializingDefault, setInitializingDefault] = useState(false);
  const [cdpDataMissing, setCdpDataMissing] = useState(false);
  const [cdpMissingReason, setCdpMissingReason] = useState<string | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [leadAnalysisLoading, setLeadAnalysisLoading] = useState(false);
  const leadAnalysisRequestRef = useRef(0);

  const {
    versions,
    selectedVersionId,
    versionsLoading,
    onVersionChange,
    refetchVersions,
  } = useNurturingPlanVersions(dealerId ?? '');

  /** Map API envelope → left-panel config + generated plan snapshot. */
  const applySettingsFromResponse = useCallback((apiRes: unknown) => {
    const body = extractSettingsBody(apiRes);
    const mapped = requestBodyToConfig(body);
    const savedPlan = extractGeneratedPlan(body);
    if (mapped) {
      const loaded = { rawConfig: mapped.rawConfig, segments: mapped.segments };
      setRawConfig(loaded.rawConfig);
      setSegments(loaded.segments);
      setOriginal(loaded);
      setLastGeneratedSnapshot(savedPlan ? loaded : null);
    } else {
      setLastGeneratedSnapshot(null);
    }
    setPlan(savedPlan);
    setPersistedPlan(savedPlan);
  }, []);

  // Show the Initialize action only when a dealer is selected and no plan config
  // exists yet (mirrors Nurturing Journey Settings).
  const showInitialize = !!dealerId && !versionsLoading && versions.length === 0;

  const selectedVersion = versions.find((v) => v._id === selectedVersionId);
  const selectedVersionNumber = selectedVersion?.nurturing_plan_version ?? null;
  const isAlreadyPublished = selectedVersion?.nurturing_plan_status === 'PUBLISHED';
  const hasPublishedVersion = versions.some(
    (v) => v.nurturing_plan_status === 'PUBLISHED',
  );

  // Full config (raw fields + segments with derived counts).
  const config = useMemo<PlanConfig>(() => {
    const warmupSchedule = rawConfig.warmupSchedule ?? [];
    const ramp =
      warmupSchedule.length > 0
        ? warmupSchedule[warmupSchedule.length - 1].dayTo
        : rawConfig.ramp;
    const cap =
      warmupSchedule.length > 0
        ? warmupSchedule[warmupSchedule.length - 1].emails
        : rawConfig.cap;
    return {
      ...rawConfig,
      warmupSchedule,
      cap,
      ramp,
      rampCad: 1,
      ssCad: 1,
      newStart: Math.max(1, rawConfig.newStart),
      segs: segsWithCounts(segments, rawConfig.total),
    };
  }, [rawConfig, segments]);

  const currentSnapshot = useMemo<EditableConfig>(
    () => ({ rawConfig, segments }),
    [rawConfig, segments],
  );

  // Enable only when left-panel inputs differ from the last generated snapshot.
  const canGenerate = useMemo(() => {
    if (!dealerId || loading || settingsLoading || segmentsConfigError) return false;
    if (segments.length === 0) return false;
    if (!lastGeneratedSnapshot) return true;
    return configSnapshotKey(currentSnapshot) !== configSnapshotKey(lastGeneratedSnapshot);
  }, [
    dealerId,
    loading,
    settingsLoading,
    currentSnapshot,
    lastGeneratedSnapshot,
    segmentsConfigError,
    segments.length,
  ]);

  // Load dealer primary segments (DEALER_SEGMENTS_CONFIG) for dropdowns / add / initialize.
  useEffect(() => {
    if (!dealerId) {
      setAvailableSegments([]);
      setSegmentsConfigError(null);
      setSegmentsConfigLoading(false);
      return;
    }
    let cancelled = false;
    setSegmentsConfigLoading(true);
    setSegmentsConfigError(null);
    fetchAvailablePlanSegments({ dealerId })
      .then((names) => {
        if (cancelled) return;
        setAvailableSegments(names);
        if (names.length === 0) {
          setSegmentsConfigError(
            'Dealer segment settings are not configured. Configure dealer segment settings first.',
          );
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setAvailableSegments([]);
        const message =
          err?.message ||
          'Dealer segment settings are not configured. Configure dealer segment settings first.';
        setSegmentsConfigError(message);
        dispatch(notify('error', message));
      })
      .finally(() => {
        if (!cancelled) setSegmentsConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerId, dispatch]);

  // Proactive CDP readiness check when the initialize screen is shown.
  useLayoutEffect(() => {
    if (!showInitialize || !dealerId) {
      setCdpDataMissing(false);
      setCdpMissingReason(null);
      setReadinessLoading(false);
      return;
    }
    let cancelled = false;
    setReadinessLoading(true);
    fetchNurturingPlanInitializeReadiness({ dealerId })
      .then((readiness) => {
        if (cancelled) return;
        if (!readiness.ready) {
          setCdpDataMissing(true);
          setCdpMissingReason(
            readiness.reason ||
              'CDP data is not available. Upload CDP data or initialize with default settings.',
          );
        } else {
          setCdpDataMissing(false);
          setCdpMissingReason(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setCdpDataMissing(false);
        setCdpMissingReason(null);
      })
      .finally(() => {
        if (!cancelled) setReadinessLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showInitialize, dealerId]);

  // Load the saved config for the selected dealer + version. Falls back to the
  // current (default) config when the backend has nothing / errors, so the
  // generator stays interactive regardless of backend readiness.
  useEffect(() => {
    if (!dealerId || !selectedVersionId) return;
    let cancelled = false;
    setSettingsLoading(true);
    setPlan(null);
    setPersistedPlan(null);
    setLastGeneratedSnapshot(null);
    fetchNurturingPlanSettings({
      dealershipId: dealerId,
      version: selectedVersionNumber,
    })
      .then((res) => {
        if (cancelled) return;
        applySettingsFromResponse(res);
      })
      .catch(() => {
        /* keep current config; backend may not be ready */
      })
      .finally(() => {
        if (!cancelled) setSettingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerId, selectedVersionId, selectedVersionNumber, applySettingsFromResponse]);

  // Clear displayed plan when dealer is deselected.
  useEffect(() => {
    if (!dealerId) {
      setPlan(null);
      setPersistedPlan(null);
      setLastGeneratedSnapshot(null);
      setRawConfig({ ...EMPTY_RAW_CONFIG });
      setSegments([]);
      setOriginal({ rawConfig: { ...EMPTY_RAW_CONFIG }, segments: [] });
    }
  }, [dealerId]);

  const handleGeneratePlan = useCallback(async () => {
    if (!dealerId || loading || !canGenerate) return;
    setLoading(true);
    try {
      const result = await generateNurturingPlan(config, dealerId);
      setPlan(result);
      setLastGeneratedSnapshot({ rawConfig, segments });
    } catch (err: any) {
      dispatch(
        notify('error', err?.message || 'Failed to generate nurturing plan'),
      );
    } finally {
      setLoading(false);
    }
  }, [dealerId, loading, canGenerate, config, rawConfig, segments, dispatch]);

  const isDirty = useMemo(
    () =>
      JSON.stringify({ rawConfig, segments }) !==
      JSON.stringify(original),
    [rawConfig, segments, original],
  );

  // Save only after Generate Plan has run for the current left-panel inputs.
  const canSave = useMemo(() => {
    if (!dealerId || !isDirty || loading || settingsLoading || saving) return false;
    if (!plan || !lastGeneratedSnapshot) return false;
    return (
      configSnapshotKey(currentSnapshot) === configSnapshotKey(lastGeneratedSnapshot)
    );
  }, [
    dealerId,
    isDirty,
    loading,
    settingsLoading,
    saving,
    plan,
    lastGeneratedSnapshot,
    currentSnapshot,
  ]);

  const onConfigChange = useCallback((field: keyof RawConfig, value: number) => {
    setRawConfig((prev) => ({ ...prev, [field]: Number.isNaN(value) ? 0 : value }));
  }, []);

  const onWarmupScheduleChange = useCallback((warmupSchedule: WarmupStep[]) => {
    setRawConfig((prev) => ({ ...prev, warmupSchedule }));
  }, []);

  const onEagerChange = useCallback((eagerProvisioning: boolean) => {
    setRawConfig((prev) => ({ ...prev, eagerProvisioning }));
  }, []);

  const onLookBackRangeChange = useCallback(
    async (range: { startDate: string | null; endDate: string | null }) => {
      setRawConfig((prev) => ({
        ...prev,
        look_back_start_date: range.startDate,
        look_back_end_date: range.endDate,
      }));

      if (!dealerId || !range.startDate || !range.endDate) {
        return;
      }

      if (
        range.startDate &&
        range.endDate &&
        range.startDate > range.endDate
      ) {
        dispatch(
          notify(
            'error',
            'Look back start date cannot be after look back end date.',
          ),
        );
        return;
      }

      const requestId = ++leadAnalysisRequestRef.current;
      setLeadAnalysisLoading(true);
      try {
        const analysis = await fetchLeadAnalysis({
          dealerId,
          lookBackStartDate: range.startDate,
          lookBackEndDate: range.endDate,
        });
        if (requestId !== leadAnalysisRequestRef.current) {
          return;
        }

        const mapped = mapLeadAnalysisToPlanFields(analysis, range);
        setRawConfig((prev) => ({
          ...prev,
          look_back_start_date: mapped.look_back_start_date,
          look_back_end_date: mapped.look_back_end_date,
          total: mapped.total,
          monthly: mapped.monthly,
          look: mapped.look,
        }));
        setSegments(mapped.segments);

        const infoMessages = (analysis.messages ?? []).filter(Boolean);
        if (infoMessages.length > 0) {
          dispatch(notify('error', infoMessages.join(' ')));
        }
      } catch (err: any) {
        if (requestId !== leadAnalysisRequestRef.current) {
          return;
        }
        dispatch(
          notify(
            'error',
            err?.message ||
              'Failed to load lead analysis for the selected look back range.',
          ),
        );
      } finally {
        if (requestId === leadAnalysisRequestRef.current) {
          setLeadAnalysisLoading(false);
        }
      }
    },
    [dealerId, dispatch],
  );

  const onSegmentNameChange = useCallback((idx: number, name: string) => {
    setSegments((prev) => prev.map((s, i) => (i === idx ? { ...s, name } : s)));
  }, []);

  const onSegmentStartDayChange = useCallback((idx: number, startDay: number) => {
    setSegments((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, startDay: startDay || 1 } : s)),
    );
  }, []);

  // Pool % distribution stays in the UI (`segmentRebalance.rebalancePct`).
  const onPctChange = useCallback((idx: number, rawVal: number) => {
    const newPct = Math.round(Number.isNaN(rawVal) ? 1 : rawVal);
    setSegments((prev) => rebalancePct(prev, idx, newPct));
  }, []);

  const canAddSegment = useMemo(() => {
    if (segmentsConfigError || segmentsConfigLoading || availableSegments.length === 0) {
      return false;
    }
    return !!firstUnusedSegmentName(availableSegments, segments);
  }, [segmentsConfigError, segmentsConfigLoading, availableSegments, segments]);

  const onAddSegment = useCallback(() => {
    const name = firstUnusedSegmentName(availableSegments, segments);
    if (!name) return;
    setSegments((prev) => addSegmentWithName(prev, name));
  }, [availableSegments, segments]);

  const onDeleteSegment = useCallback((idx: number) => {
    setSegments((prev) => deleteSegment(prev, idx));
  }, []);

  const onReorderSegments = useCallback((activeId: string, overId: string) => {
    setSegments((prev) => reorderSegments(prev, activeId, overId));
  }, []);

  const toggleBatch = useCallback((key: string) => {
    setOpenBatches((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleCancel = useCallback(() => {
    setRawConfig(original.rawConfig);
    setSegments(original.segments);
    setPlan(persistedPlan);
    setLastGeneratedSnapshot(
      persistedPlan ? { rawConfig: original.rawConfig, segments: original.segments } : null,
    );
  }, [original, persistedPlan]);

  const finishInitializeFromResponse = useCallback(
    async (res: unknown) => {
      if (!dealerId) return;
      applySettingsFromResponse(res);
      const versionList = await refetchVersions();
      const newest = versionList[0];
      if (newest?.nurturing_plan_version != null) {
        const settingsRes = await fetchNurturingPlanSettings({
          dealershipId: dealerId,
          version: newest.nurturing_plan_version,
        });
        applySettingsFromResponse(settingsRes);
      }
      setCdpDataMissing(false);
      setCdpMissingReason(null);
    },
    [dealerId, refetchVersions, applySettingsFromResponse],
  );

  const handleInitialize = useCallback(async () => {
    if (!dealerId || initializing) return;
    setInitializing(true);
    try {
      const res = await initializeNurturingPlanSettings({ dealerId });
      await finishInitializeFromResponse(res);
      dispatch(
        notify('success', 'Nurturing plan settings initialized successfully'),
      );
    } catch (err: any) {
      const message =
        err?.message || 'Failed to initialize nurturing plan settings';
      setCdpDataMissing(true);
      setCdpMissingReason(message);
      dispatch(notify('error', message));
    } finally {
      setInitializing(false);
    }
  }, [dealerId, initializing, finishInitializeFromResponse, dispatch]);

  const handleInitializeDefault = useCallback(async () => {
    if (!dealerId || initializingDefault || segmentsConfigError) return;
    setInitializingDefault(true);
    try {
      const res = await initializeNurturingPlanSettingsDefault({ dealerId });
      await finishInitializeFromResponse(res);
      dispatch(
        notify(
          'success',
          'Nurturing plan initialized with default settings successfully',
        ),
      );
    } catch (err: any) {
      const message =
        err?.message || 'Failed to initialize nurturing plan with default settings';
      dispatch(notify('error', message));
    } finally {
      setInitializingDefault(false);
    }
  }, [
    dealerId,
    initializingDefault,
    segmentsConfigError,
    finishInitializeFromResponse,
    dispatch,
  ]);

  const handleUploadCdpData = useCallback(() => {
    history.push('/configure-cdp-settings', {
      breadcrumbsList: [
        { label: 'Configure', link: '/configure', isActive: false },
        { label: 'Nurturing Plan Generator', link: '/nurturing-plan-generator', isActive: false },
        { label: 'CDP Settings', isActive: true },
      ],
      returnTo: '/nurturing-plan-generator',
    });
  }, [history]);

  const handleSave = useCallback(async () => {
    if (!dealerId || !canSave || saving || !plan) return;
    setSaving(true);
    try {
      const computedPlan = plan;
      await saveNurturingPlanSettings({
        dealershipId: dealerId,
        payload: { dealer_id: dealerId, ...buildSavePayload(config, computedPlan) },
        version: selectedVersionNumber,
      });
      dispatch(notify('success', 'Nurturing plan settings saved successfully'));
      setPlan(computedPlan);
      setPersistedPlan(computedPlan);
      setLastGeneratedSnapshot({ rawConfig, segments });
      // Sync the dirty baseline immediately; refetch reloads the latest version.
      setOriginal({ rawConfig, segments });
      await refetchVersions();
    } catch (err: any) {
      dispatch(
        notify('error', err?.message || 'Failed to save nurturing plan settings'),
      );
    } finally {
      setSaving(false);
    }
  }, [
    dealerId,
    canSave,
    saving,
    config,
    plan,
    selectedVersionNumber,
    rawConfig,
    segments,
    refetchVersions,
    dispatch,
  ]);

  const handlePublish = useCallback(async () => {
    if (!dealerId || !selectedVersionId || publishing) return;
    setPublishing(true);
    try {
      await publishNurturingPlan({ dealerId, settingsId: selectedVersionId });
      dispatch(notify('success', 'Nurturing plan published successfully'));
      await refetchVersions();
    } catch (err: any) {
      dispatch(notify('error', err?.message || 'Failed to publish nurturing plan'));
    } finally {
      setPublishing(false);
    }
  }, [dealerId, selectedVersionId, publishing, refetchVersions, dispatch]);

  const handleLaunch = useCallback(async () => {
    if (
      !dealerId ||
      launching ||
      hasLaunchDate ||
      showLaunchMaterializationStatus ||
      !hasPublishedVersion
    ) {
      return;
    }
    setLaunching(true);
    try {
      const response = await launchDealerForNurturingPlan({ dealerId });
      const statusText: string | undefined = response?.statusText;

      const launchAck = response?.body ?? response?.data;
      if (statusText && statusText !== 'success') {
        throw new Error(response?.message || 'Failed to launch dealership');
      }

      const nextSelectedDealer = mergeDealerWithLaunchAck(selectedDealer, launchAck);
      if (nextSelectedDealer) {
        dispatch(headerActions.selectDealer(nextSelectedDealer));
        persistSelectedDealerToLocalStorage(nextSelectedDealer);
      }

      dispatch(
        notify(
          'success',
          response?.message ||
            'Dealer launch accepted. Calendar materialization is running in the background.',
        ),
      );
    } catch (err: any) {
      dispatch(
        notify(
          'error',
          err?.message || 'Failed to launch dealership',
        ),
      );
    } finally {
      setLaunching(false);
    }
  }, [dealerId, launching, hasLaunchDate, showLaunchMaterializationStatus, hasPublishedVersion, dispatch, selectedDealer]);

  const pctSum = useMemo(
    () => segments.reduce((s, g) => s + g.pct, 0),
    [segments],
  );

  return {
    config,
    rawConfig,
    segments,
    plan,
    loading,
    settingsLoading,
    dealerId,
    activeTab,
    setActiveTab,
    openBatches,
    toggleBatch,
    pctSum,
    onConfigChange,
    onWarmupScheduleChange,
    onEagerChange,
    onLookBackRangeChange,
    leadAnalysisLoading,
    onSegmentNameChange,
    onSegmentStartDayChange,
    onPctChange,
    onAddSegment,
    onDeleteSegment,
    onReorderSegments,
    // Versioning + save/publish
    versions,
    selectedVersionId,
    versionsLoading,
    onVersionChange,
    isDirty,
    canSave,
    isAlreadyPublished,
    hasPublishedVersion,
    saving,
    publishing,
    launching,
    initializing,
    initializingDefault,
    showInitialize,
    cdpDataMissing,
    cdpMissingReason,
    readinessLoading,
    checkingInitializeEligibility:
      showInitialize && (segmentsConfigLoading || readinessLoading),
    handleCancel,
    handleSave,
    handlePublish,
    handleLaunch,
    handleInitialize,
    handleInitializeDefault,
    handleUploadCdpData,
    handleGeneratePlan,
    canGenerate,
    availableSegments,
    segmentsConfigError,
    segmentsConfigLoading,
    canAddSegment,
    hasLaunchDate,
    showLaunchMaterializationStatus,
    isLaunchMaterializationInProgress,
    selectedDealer,
    launchDateDisplay,
  };
};

export type NurturingPlanController = ReturnType<typeof useNurturingPlan>;
